'use client';

import type { Chapter45Anchor } from '../../chapter45';
import {
  anchorKey,
  decodeHtmlEntities,
  stripCurlyBraces,
  unescapeMarkdown,
} from './utils';

// ============================================================================
// HTML Tag Handling
// ============================================================================

const VOID_ELEMENTS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

function findOpenTagsAtPosition(
  text: string,
  position: number,
): Array<{ tagName: string; startPos: number }> {
  const openTags: Array<{ tagName: string; startPos: number }> = [];
  const tagPattern = /<\/?([a-zA-Z][a-zA-Z0-9]*)[^>]*>/g;
  let match;

  while ((match = tagPattern.exec(text)) !== null) {
    if (match.index >= position) break;

    const fullMatch = match[0];
    const tagName = match[1]!.toLowerCase();

    if (fullMatch.startsWith('</')) {
      const idx = openTags.findLastIndex((t) => t.tagName === tagName);
      if (idx !== -1) {
        openTags.splice(idx, 1);
      }
    } else if (!fullMatch.endsWith('/>') && !VOID_ELEMENTS.has(tagName)) {
      openTags.push({ tagName, startPos: match.index });
    }
  }

  return openTags;
}

function findClosingTagPosition(
  text: string,
  tagName: string,
  afterPosition: number,
): number {
  const pattern = new RegExp(`</${tagName}>`, 'gi');
  pattern.lastIndex = afterPosition;
  const match = pattern.exec(text);
  return match ? match.index + match[0].length : -1;
}

function adjustAnchorForHtmlBoundaries(
  text: string,
  anchor: Chapter45Anchor,
): Chapter45Anchor {
  let start = anchor.start;
  let end = anchor.end;

  const openTagsAtStart = findOpenTagsAtPosition(text, start);
  if (openTagsAtStart.length > 0) {
    const earliestTag = openTagsAtStart[0]!;
    start = earliestTag.startPos;
  }

  const openTagsAtEnd = findOpenTagsAtPosition(text, end);
  for (const tag of openTagsAtEnd) {
    if (tag.startPos < anchor.start) {
      const closingPos = findClosingTagPosition(text, tag.tagName, end);
      if (closingPos > end) {
        end = closingPos;
      }
    }
  }

  let inTag = false;
  let tagStart = 0;
  for (let i = 0; i < start; i++) {
    if (text[i] === '<') {
      inTag = true;
      tagStart = i;
    } else if (text[i] === '>') {
      inTag = false;
    }
  }
  if (inTag) {
    start = tagStart;
  }

  inTag = false;
  for (let i = 0; i < end; i++) {
    if (text[i] === '<') {
      inTag = true;
    } else if (text[i] === '>') {
      inTag = false;
    }
  }
  if (inTag) {
    const closeIdx = text.indexOf('>', end);
    if (closeIdx !== -1) {
      end = closeIdx + 1;
    }
  }

  return { ...anchor, start, end };
}

function adjustAnchorForRemovedBraces(
  originalText: string,
  anchor: Chapter45Anchor,
): Chapter45Anchor {
  let bracesBeforeStart = 0;
  for (let i = 0; i < anchor.start && i < originalText.length; i++) {
    if (originalText[i] === '{' || originalText[i] === '}') {
      bracesBeforeStart++;
    }
  }

  let bracesBeforeEnd = 0;
  for (let i = 0; i < anchor.end && i < originalText.length; i++) {
    if (originalText[i] === '{' || originalText[i] === '}') {
      bracesBeforeEnd++;
    }
  }

  return {
    ...anchor,
    start: anchor.start - bracesBeforeStart,
    end: anchor.end - bracesBeforeEnd,
  };
}

// ============================================================================
// Inline Markdown Parsing
// ============================================================================

function parseInlineMarkdown(text: string, keyPrefix: string): React.ReactNode {
  const pattern =
    /(\*\*\*[^*]+\*\*\*|\*\*[^*]+\*\*|\*[^*]+\*|<b>[\s\S]*?<\/b>|<i>[\s\S]*?<\/i>|<u>[\s\S]*?<\/u>|<sup>[\s\S]*?<\/sup>|<sub>[\s\S]*?<\/sub>|<br\s*\/?>)/gi;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(
        <span key={`${keyPrefix}-t-${lastIndex}`}>
          {unescapeMarkdown(text.slice(lastIndex, match.index))}
        </span>,
      );
    }

    const matched = match[0];
    const matchedLower = matched.toLowerCase();

    if (matched.startsWith('***') && matched.endsWith('***')) {
      parts.push(
        <b key={`${keyPrefix}-bi-${match.index}`} className="font-bold italic">
          {parseInlineMarkdown(
            matched.slice(3, -3),
            `${keyPrefix}-bi-${match.index}`,
          )}
        </b>,
      );
    } else if (matched.startsWith('**') && matched.endsWith('**')) {
      parts.push(
        <b key={`${keyPrefix}-b-${match.index}`} className="font-bold">
          {parseInlineMarkdown(
            matched.slice(2, -2),
            `${keyPrefix}-b-${match.index}`,
          )}
        </b>,
      );
    } else if (
      matchedLower.startsWith('<u>') &&
      matchedLower.endsWith('</u>')
    ) {
      parts.push(
        <u key={`${keyPrefix}-u-${match.index}`}>
          {parseInlineMarkdown(
            matched.slice(3, -4),
            `${keyPrefix}-u-${match.index}`,
          )}
        </u>,
      );
    } else if (
      matchedLower.startsWith('<sup>') &&
      matchedLower.endsWith('</sup>')
    ) {
      parts.push(
        <sup key={`${keyPrefix}-sup-${match.index}`} className="text-[0.75em]">
          {parseInlineMarkdown(
            matched.slice(5, -6),
            `${keyPrefix}-sup-${match.index}`,
          )}
        </sup>,
      );
    } else if (
      matchedLower.startsWith('<sub>') &&
      matchedLower.endsWith('</sub>')
    ) {
      parts.push(
        <sub key={`${keyPrefix}-sub-${match.index}`} className="text-[0.75em]">
          {parseInlineMarkdown(
            matched.slice(5, -6),
            `${keyPrefix}-sub-${match.index}`,
          )}
        </sub>,
      );
    } else if (
      matchedLower.startsWith('<b>') &&
      matchedLower.endsWith('</b>')
    ) {
      parts.push(
        <b key={`${keyPrefix}-hb-${match.index}`} className="font-bold">
          {parseInlineMarkdown(
            matched.slice(3, -4),
            `${keyPrefix}-hb-${match.index}`,
          )}
        </b>,
      );
    } else if (
      matchedLower.startsWith('<i>') &&
      matchedLower.endsWith('</i>')
    ) {
      parts.push(
        <i key={`${keyPrefix}-hi-${match.index}`} className="italic">
          {parseInlineMarkdown(
            matched.slice(3, -4),
            `${keyPrefix}-hi-${match.index}`,
          )}
        </i>,
      );
    } else if (/^<br\s*\/?>$/i.test(matched)) {
      parts.push(<br key={`${keyPrefix}-br-${match.index}`} />);
    } else if (matched.startsWith('*') && matched.endsWith('*')) {
      parts.push(
        <i key={`${keyPrefix}-i-${match.index}`} className="italic">
          {parseInlineMarkdown(
            matched.slice(1, -1),
            `${keyPrefix}-i-${match.index}`,
          )}
        </i>,
      );
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(
      <span key={`${keyPrefix}-t-${lastIndex}`}>
        {unescapeMarkdown(text.slice(lastIndex))}
      </span>,
    );
  }

  return parts.length > 0 ? parts : unescapeMarkdown(text);
}

function renderInlineFormatting(
  text: string,
  keyPrefix: string,
): React.ReactNode {
  const cleanText = stripCurlyBraces(text);
  const decodedText = decodeHtmlEntities(cleanText);
  return parseInlineMarkdown(decodedText, keyPrefix);
}

// ============================================================================
// HighlightedText Component
// ============================================================================

interface HighlightedTextProps {
  text: string;
  ranges?: Chapter45Anchor[];
  firstAnchors?: Set<string>;
  lastAnchors?: Set<string>;
}

export function HighlightedText({
  text,
  ranges,
  firstAnchors,
  lastAnchors,
}: HighlightedTextProps) {
  const cleanText = stripCurlyBraces(text);

  if (!ranges || ranges.length === 0) {
    return <>{renderInlineFormatting(cleanText, 'hl')}</>;
  }

  let adjustedRanges = ranges.map((r) => adjustAnchorForRemovedBraces(text, r));

  const hasHtmlTags = /<[^>]+>/.test(cleanText);
  if (hasHtmlTags) {
    adjustedRanges = adjustedRanges.map((r) =>
      adjustAnchorForHtmlBoundaries(cleanText, r),
    );
  }

  const parts: React.ReactNode[] = [];
  let pos = 0;

  for (let i = 0; i < adjustedRanges.length; i++) {
    const r = adjustedRanges[i]!;
    const originalR = ranges[i]!;
    const start = Math.max(0, Math.min(cleanText.length, r.start));
    const end = Math.max(0, Math.min(cleanText.length, r.end));
    if (end <= start) continue;

    if (start > pos) {
      parts.push(
        <span key={`t-${pos}`}>
          {renderInlineFormatting(cleanText.slice(pos, start), `t-${pos}`)}
        </span>,
      );
    }

    const key = anchorKey(originalR);
    const isFirst = firstAnchors?.has(key) ?? false;
    const isLast = lastAnchors?.has(key) ?? false;

    parts.push(
      <mark
        key={`m-${start}-${end}`}
        data-ch45-quote-mark=""
        data-quote-index={r.quoteIndex}
        data-quote-first={isFirst ? 'true' : undefined}
        data-quote-last={isLast ? 'true' : undefined}
      >
        {renderInlineFormatting(cleanText.slice(start, end), `m-${start}`)}
      </mark>,
    );

    pos = end;
  }

  if (pos < cleanText.length) {
    parts.push(
      <span key={`t-${pos}`}>
        {renderInlineFormatting(cleanText.slice(pos), `t-${pos}`)}
      </span>,
    );
  }

  return <>{parts}</>;
}
